import {useCadencier, useListes} from '@/haligator-factory';
import {HaliRenderOne, useHalCollection} from '@synako/haligator';
import {Box, HStack, Text, VStack} from '@chakra-ui/layout';
import {Divider, IconButton, Image, Input, Tooltip} from '@chakra-ui/react';
import {
  AddIcon,
  ArrowForwardIcon,
  CheckIcon,
  CloseIcon,
  EditIcon,
  ViewIcon,
} from '@chakra-ui/icons';
import {TrashIcon} from '@/components/svg/TrashIcon';
import {Link, useNavigate} from 'react-router-dom';
import {routes} from 'ff-common';
import {useCallback, useState} from 'react';
import {ListeDesc} from 'api-types/ffconnect-hal';
import {State} from '@synako/halitrek';
import NiceModal from '@ebay/nice-modal-react';
import {useTranslation} from 'react-i18next';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {ModalCreateListe} from './ModalCreateListe';
import {BlueButton} from '@/components/Button/BlueButton';
import {Chargement} from '@/components/Chargement/Chargement';
import {ModaleAlerteConfirmation} from '../../panier/PanierFound/ModaleAlerteConfirmation';
import {ErrorBoundary} from 'react-error-boundary';

// Contenu des listes existantes
export const ListesContenu: React.FC = () => {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const {buildClientRoute} = useClientRoute();

  const {items, refreshing, loading} = useHalCollection(useListes());
  // Composant interne qui calcule et affiche le nombre d'items d'une liste
  const NombreProduitsInline: React.FC<{liste: State<ListeDesc>}> = ({
    liste,
  }) => {
    const items = liste.followAllOrThrow('selection');
    return <Text fontSize='sm'>{items.length}</Text>;
  };

  const [editeListeId, setEditeListeId] = useState<string | null>(null);
  const [nouveauNom, setNouveauNom] = useState('');

  const handleClickCadencier = () => {
    navigate(buildClientRoute(routes.client.cadencier));
  };

  const handleDeleteList = (id: string, listeState: State<ListeDesc>) => {
    NiceModal.show(ModaleAlerteConfirmation, {
      validate_texte: t('liste.supprimer_liste_action'),
      body: t('liste.supprimer_liste_confirm'),
      validateAction: () => {
        deleteListAction(id, listeState);
      },
    });
  };

  const handleRenommerListe = (id: string, listeState: State<ListeDesc>) => {
    renameListAction(id, listeState, nouveauNom);
    setEditeListeId(null);
  };

  const handleEditClick = (id: string, currentName: string) => {
    setEditeListeId(id);
    setNouveauNom(currentName);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNouveauNom(e.target.value);
  };

  const deleteListAction = useCallback(
    async (listeId: string, listeState: State<ListeDesc>) => {
      try {
        await listeState.submitCommand('c:supprimer-une-liste', {
          liste_id: listeId,
        });
        textToast(t('liste.liste_supprimee', {nom: listeState.data.nom_liste}));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        onError(error);
      }
    },
    [textToast, t, onError],
  );

  const renameListAction = useCallback(
    async (
      listeId: string,
      listeState: State<ListeDesc>,
      nouveauNomListe: string,
    ) => {
      try {
        await listeState.submitCommand('c:modifier-nom-d-une-liste', {
          liste_id: listeId,
          nouveau_nom_liste: nouveauNomListe,
        });
        textToast(t('liste.liste_renommee'));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        onError(error);
      }
    },
    [textToast, t, onError],
  );

  return (
    <VStack
      align='stretch'
      spacing={4}
      p='20px'
    >
      {/* Bouton pour accéder au cadencier */}
      <HStack
        bg='lblue.150'
        borderRadius='8px'
        padding='20px'
        p='20px 20px 0px 40px'
        mt={{base: '0', md: '20px'}}
        justifyContent='space-between'
        cursor='pointer'
        _hover={{opacity: 0.8}}
        onClick={handleClickCadencier}
      >
        <VStack
          justify='space-between'
          alignItems='start'
        >
          <Text
            fontWeight='700'
            color='blue.500'
            fontSize={{md: 'lg', base: 'sm'}}
            mt='20px'
          >
            {t('cadencier.mon_cadencier')} <ArrowForwardIcon />
          </Text>
          <HaliRenderOne
            nav={useCadencier()}
            render={({data}) => {
              return (
                <Text
                  color='blue.500'
                  fontSize={{md: 'md', base: 'sm'}}
                  mb='30px'
                >
                  {t('global.produit', {count: data.nombre_articles})}
                </Text>
              );
            }}
          />
        </VStack>

        <Box
          display={{base: 'none', md: 'flex'}}
          alignItems='flex-end'
          alignSelf='flex-end'
        >
          <Image
            src={`${import.meta.env.BASE_URL}/cadencier_bouton_3.webp`}
            width='245px'
          />
        </Box>
      </HStack>

      <HStack justifyContent='space-between'>
        {/*Compteur de nbre de listes*/}
        <Text
          color='gray.dark'
          fontSize='sm'
          fontWeight='600'
          bg='gray.light'
          p='5px 10px'
          borderRadius='5px'
          height='42px'
          textAlign='center'
          display='flex'
          alignItems='center'
          justifyContent='center'
        >
          {t('panier.listes', {count: items?.length})}
        </Text>
        {/* Bouton pour créer une nouvelle liste */}
        <HStack justifyContent='space-between'>
          <BlueButton
            fontSize='sm'
            texte={t('liste.creer_liste')}
            onClick={() => NiceModal.show(ModalCreateListe)}
            leftIcon={<AddIcon />}
            mt='0'
            height='42px'
          />
        </HStack>
      </HStack>

      {loading || refreshing ? (
        <Chargement texte={t('liste.chargement_listes')} />
      ) : (
        items?.map((liste) => {
          if (!liste.data.code_liste) {
            return null;
          }
          const isEditing = editeListeId === liste.data.id;
          return (
            <HStack
              key={liste.data.id}
              justify='space-between'
              _hover={{bg: 'gray.bg'}}
              border='1px solid'
              borderColor='gray.light'
              borderRadius='12px'
              p='20px 20px 20px 40px'
            >
              <HStack spacing={2}>
                {isEditing ? (
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        handleRenommerListe(liste.data.id, liste);
                      }}
                    >
                      <Input
                        value={nouveauNom}
                        onChange={handleInputChange}
                        onSubmit={(e) => {
                          e.preventDefault();
                          handleRenommerListe(liste.data.id, liste);
                        }}
                        autoFocus
                        bg='white'
                      />
                    </form>
                    <IconButton
                      aria-label={t('liste.aria_label_enregistrer')}
                      icon={<CheckIcon />}
                      onClick={() => {
                        handleRenommerListe(liste.data.id, liste);
                      }}
                      size='sm'
                      bg='blue.main'
                      _hover={{bg: 'blue.300'}}
                      color='white'
                    />
                    <IconButton
                      aria-label='Annuler'
                      icon={<CloseIcon />}
                      onClick={() => {
                        setEditeListeId(null);
                      }}
                      size='sm'
                    />
                  </>
                ) : (
                  <>
                    <Link
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'end',
                        alignItems: 'center',
                      }}
                      to={buildClientRoute(routes.client.listes, {
                        code_liste: liste.data.code_liste ?? '',
                      })}
                    >
                      <Text
                        fontSize={{md: 'lg', base: 'sm'}}
                        fontWeight='700'
                        whiteSpace='nowrap'
                        overflow='hidden'
                        textOverflow='ellipsis'
                      >
                        {liste.data.nom_liste}
                      </Text>
                    </Link>
                    <Tooltip
                      label={t('liste.aria_label_modifier')}
                      hasArrow
                    >
                      <IconButton
                        aria-label={t('liste.editer_nom_liste')}
                        icon={<EditIcon />}
                        onClick={() => {
                          handleEditClick(liste.data.id, liste.data.nom_liste);
                        }}
                        size='sm'
                        variant='ghost'
                      />
                    </Tooltip>
                  </>
                )}
                {!isEditing && (
                  <Link
                    style={{
                      flex: 1,
                      display: 'flex',
                      justifyContent: 'end',
                      alignItems: 'center',
                    }}
                    to={buildClientRoute(routes.client.listes, {
                      code_liste: liste.data.code_liste ?? '',
                    })}
                  >
                    <Box
                      p='2px 5px 2px 5px'
                      bg='gray.light'
                      borderRadius='5px'
                    >
                      <ErrorBoundary fallbackRender={() => null}>
                        <NombreProduitsInline liste={liste} />
                      </ErrorBoundary>
                    </Box>
                  </Link>
                )}
              </HStack>
              <Link
                style={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'end',
                  alignItems: 'center',
                }}
                to={buildClientRoute(routes.client.listes, {
                  code_liste: liste.data.code_liste ?? '',
                })}
              >
                <HStack
                  justifyContent='end'
                  mr='20px'
                  alignItems='center'
                  lineHeight='1'
                >
                  <ViewIcon color='gray.dark' />
                  <Text
                    color='gray.dark'
                    fontSize='sm'
                    lineHeight='1'
                    display={{base: 'none', md: 'inline'}}
                  >
                    {t('global.consulter')}
                  </Text>
                </HStack>
              </Link>
              <Divider
                orientation='vertical'
                height='30px'
                borderColor='gray.light'
              />
              <HStack
                ml='15px'
                onClick={() => {
                  handleDeleteList(liste.data.id, liste);
                }}
                cursor='pointer'
              >
                <TrashIcon color='gray.dark' />
              </HStack>
            </HStack>
          );
        })
      )}
    </VStack>
  );
};
